import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { MailWarning, Send, CheckCircle2, LogOut, PencilLine } from 'lucide-react';
import { getMe, resendVerification, requestEmailChange } from '@/lib/api';

// Roles exempt from the email-verify gate (staff tiers + admin). The gate
// applies to regular "User" accounts only.
const GATE_EXEMPT_ROLES = ['Admin', 'Maestro', 'Manager', 'Staff', 'Famiglia'];

interface Props {
    user: any;
}

/**
 * Blocking full-screen gate that forces email verification for unverified
 * non-exempt users. FAIL-OPEN: only shown when the user is known and
 * email_verified is strictly false. Never blocks on unknown/errored state.
 *
 * TYPO RECOVERY: besides resending to the current address, the gate lets a user
 * who mistyped their email RE-ENTER a corrected address (with their current
 * password) via /users/me/request-email-change, which mails a confirm link to
 * the NEW address. Clicking that link commits the change server-side and the 5s
 * poll below auto-dismisses the gate — so a typo user is never trapped.
 */
export function EmailVerifyGate({ user }: Props) {
    const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
    const [message, setMessage] = useState('');
    const [dismissedByVerify, setDismissedByVerify] = useState(false);
    const pollRef = useRef<number | null>(null);

    // Email-correction sub-form state.
    const [showChange, setShowChange] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [changeStatus, setChangeStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
    const [changeMessage, setChangeMessage] = useState('');

    // Only block when we positively know the user is unverified and not exempt.
    // Also stand down while the mandatory initial-credentials setup is pending
    // (that flow has its own modal and sets email_verified=True on completion),
    // to avoid a z-order collision / lockout.
    const shouldBlock =
        !!user &&
        !!user.email && String(user.email).trim() !== '' &&
        user.email_verified === false &&
        !user.must_change_credentials &&
        !GATE_EXEMPT_ROLES.includes(user.role_type) &&
        !dismissedByVerify;

    // Poll /users/me while blocked; auto-dismiss once verification completes
    // (e.g. the user clicked the email link in another tab).
    useEffect(() => {
        if (!shouldBlock) {
            if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
            return;
        }
        const check = async () => {
            try {
                const res = await getMe();
                if (res?.data?.email_verified) {
                    localStorage.setItem('user', JSON.stringify(res.data));
                    setDismissedByVerify(true);
                    window.dispatchEvent(new Event('maison:user-updated'));
                }
            } catch {
                // Fail-open: ignore transient errors, do not lock the user out.
            }
        };
        pollRef.current = window.setInterval(check, 5000);
        return () => {
            if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
        };
    }, [shouldBlock]);

    if (!shouldBlock) return null;

    const handleResend = async () => {
        if (status === 'sending') return;
        setStatus('sending');
        setMessage('');
        try {
            const res = await resendVerification();
            if (res?.data?.email_verified) {
                // Already verified server-side -> dismiss.
                setDismissedByVerify(true);
                window.dispatchEvent(new Event('maison:user-updated'));
                return;
            }
            setStatus('sent');
            setMessage('確認メールをお送りしました。メール内のリンクをクリックしてください。');
        } catch (err: any) {
            setStatus('error');
            const detail = err?.response?.data?.detail;
            setMessage(typeof detail === 'string' ? detail : '送信に失敗しました。時間をおいて再度お試しください。');
        }
    };

    const handleEmailChange = async () => {
        if (changeStatus === 'sending') return;
        if (!newEmail.trim() || !currentPassword) {
            setChangeStatus('error');
            setChangeMessage('新しいメールアドレスと現在のパスワードを入力してください。');
            return;
        }
        setChangeStatus('sending');
        setChangeMessage('');
        try {
            await requestEmailChange(newEmail.trim(), currentPassword);
            setChangeStatus('sent');
            setChangeMessage('新しいメールアドレス宛に確認リンクを送信しました。メール内のリンクをクリックすると変更が確定し、この画面は自動的に解除されます。');
            setCurrentPassword('');
        } catch (err: any) {
            setChangeStatus('error');
            const detail = err?.response?.data?.detail;
            setChangeMessage(typeof detail === 'string' ? detail : '変更リクエストに失敗しました。時間をおいて再度お試しください。');
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
    };

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 font-serif overflow-y-auto"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
        >
            <motion.div
                initial={{ scale: 0.92, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="w-full max-w-md bg-[#1c1917] border border-[#d4af37] p-8 text-center shadow-[0_0_50px_rgba(0,0,0,0.85)] my-8"
            >
                <div className="flex justify-center mb-5">
                    <div className="w-16 h-16 bg-[#d4af37]/15 rounded-full flex items-center justify-center border border-[#d4af37]">
                        <MailWarning className="w-8 h-8 text-[#fcd34d]" />
                    </div>
                </div>
                <h2 className="text-xl font-bold text-[#fcd34d] mb-3 tracking-widest">メールアドレスの認証が必要です</h2>
                <div className="h-px w-20 bg-[#d4af37] mx-auto my-4 opacity-50"></div>
                <p className="text-sm text-[#d4af37]/80 leading-relaxed mb-2">
                    Maison Portalをご利用いただくには、メールアドレスの認証が必要です。
                </p>
                {user?.email && (
                    <p className="text-sm text-[#fcd34d] mb-4 break-all">{user.email}</p>
                )}
                <p className="text-xs text-[#d4af37]/60 leading-relaxed mb-6">
                    下のボタンから確認メールを送信し、メール内のリンクをクリックしてください。<br />
                    認証が完了すると、この画面は自動的に解除されます。
                </p>

                {status === 'sent' && (
                    <div className="mb-4 p-3 bg-[#d4af37]/10 border border-[#d4af37]/40 text-[#fcd34d] text-sm flex items-center gap-2 justify-center">
                        <CheckCircle2 size={18} />
                        <span>{message}</span>
                    </div>
                )}
                {status === 'error' && (
                    <div className="mb-4 p-3 bg-red-950/30 border border-red-900/50 text-red-400 text-sm">
                        {message}
                    </div>
                )}

                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleResend}
                    disabled={status === 'sending'}
                    className="w-full bg-[#d4af37] hover:bg-[#ffe58f] text-black font-serif font-bold h-12 uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                    {status === 'sending' ? (
                        <span className="animate-pulse">送信中...</span>
                    ) : (
                        <>
                            <Send size={16} />
                            <span>{status === 'sent' ? '確認メールを再送する' : '確認メールを送信する'}</span>
                        </>
                    )}
                </motion.button>

                {/* Typo recovery: re-enter a corrected email address. */}
                <div className="mt-6 pt-5 border-t border-[#d4af37]/20 text-left">
                    {!showChange ? (
                        <button
                            type="button"
                            onClick={() => setShowChange(true)}
                            className="w-full text-xs text-[#d4af37]/70 hover:text-[#fcd34d] flex items-center justify-center gap-1"
                        >
                            <PencilLine size={12} />
                            メールアドレスを間違えた場合はこちら（入力し直す）
                        </button>
                    ) : (
                        <div className="space-y-3">
                            <p className="text-xs text-[#d4af37]/70 leading-relaxed text-center">
                                正しいメールアドレスを入力してください。新しいアドレス宛に確認リンクをお送りします。
                            </p>
                            <div>
                                <label className="block text-xs text-[#d4af37]/70 mb-1">新しいメールアドレス</label>
                                <input
                                    type="email"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    placeholder="correct@example.com"
                                    autoComplete="email"
                                    className="w-full bg-black/40 border border-[#d4af37]/40 text-[#fcd34d] px-3 py-2 text-sm outline-none focus:border-[#fcd34d]"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-[#d4af37]/70 mb-1">現在のパスワード（本人確認）</label>
                                <input
                                    type="password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    placeholder="パスワード"
                                    autoComplete="current-password"
                                    className="w-full bg-black/40 border border-[#d4af37]/40 text-[#fcd34d] px-3 py-2 text-sm outline-none focus:border-[#fcd34d]"
                                />
                            </div>

                            {changeStatus === 'sent' && (
                                <div className="p-3 bg-[#d4af37]/10 border border-[#d4af37]/40 text-[#fcd34d] text-xs flex items-start gap-2">
                                    <CheckCircle2 size={16} className="shrink-0 mt-0.5" />
                                    <span>{changeMessage}</span>
                                </div>
                            )}
                            {changeStatus === 'error' && (
                                <div className="p-3 bg-red-950/30 border border-red-900/50 text-red-400 text-xs">
                                    {changeMessage}
                                </div>
                            )}

                            <button
                                type="button"
                                onClick={handleEmailChange}
                                disabled={changeStatus === 'sending'}
                                className="w-full bg-transparent border border-[#d4af37] hover:bg-[#d4af37]/15 text-[#fcd34d] font-serif h-11 text-sm tracking-wider transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                            >
                                {changeStatus === 'sending' ? (
                                    <span className="animate-pulse">送信中...</span>
                                ) : (
                                    <>
                                        <Send size={14} />
                                        <span>この新しいアドレスに確認メールを送る</span>
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>

                <button
                    type="button"
                    onClick={handleLogout}
                    className="mt-5 w-full text-xs text-[#d4af37]/60 hover:text-[#fcd34d] flex items-center justify-center gap-1"
                >
                    <LogOut size={12} />
                    ログアウト
                </button>
            </motion.div>
        </div>
    );
}
